/**
 * AdventureFileUsagesDialog Component
 * Modal dialog showing where a file is used within the adventure
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material'
import {
  Close,
  LocationOn,
  Description,
  FilterList,
  HelpOutline,
} from '@mui/icons-material'
import { useAdventureFileUsages } from '../../../hooks/useAdventureFiles'
import type { MediaFileUsageDto } from '@api/types/adventureFiles'

interface AdventureFileUsagesDialogProps {
  open: boolean
  onClose: () => void
  adventureId: string | null
  fileId: string | null
  fileName?: string
}

function getEntityTypeIcon(entityType: MediaFileUsageDto['entityType']) {
  switch (entityType) {
    case 'Node':
      return <LocationOn />
    case 'Description':
      return <Description />
    case 'Criterion':
      return <FilterList />
    default:
      return <HelpOutline />
  }
}

function getEntityTypeColor(entityType: MediaFileUsageDto['entityType']): 'primary' | 'secondary' | 'info' | 'default' {
  switch (entityType) {
    case 'Node':
      return 'primary'
    case 'Description':
      return 'secondary'
    case 'Criterion':
      return 'info'
    default:
      return 'default'
  }
}

export function AdventureFileUsagesDialog({
  open,
  onClose,
  adventureId,
  fileId,
  fileName,
}: AdventureFileUsagesDialogProps) {
  const { data: usages, isLoading, error } = useAdventureFileUsages(adventureId, open ? fileId : null)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">
          File Usages
        </Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {fileName && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Showing usages for: <strong>{fileName}</strong>
          </Typography>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">
            Failed to load file usages. Please try again.
          </Alert>
        )}

        {!isLoading && !error && usages && usages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              This file is not currently used anywhere in the adventure.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              It can be safely deleted without affecting any content.
            </Typography>
          </Box>
        )}

        {!isLoading && !error && usages && usages.length > 0 && (
          <List disablePadding>
            {usages.map((usage, index) => (
              <ListItem
                key={`${usage.entityId}-${index}`}
                divider={index < usages.length - 1}
                sx={{ px: 0 }}
              >
                <ListItemIcon>
                  {getEntityTypeIcon(usage.entityType)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">
                        {usage.entityName}
                      </Typography>
                      <Chip
                        label={usage.entityType}
                        size="small"
                        color={getEntityTypeColor(usage.entityType)}
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {usage.context}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
